import React from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { Button, ButtonPriority, Text, TextTypography } from 'wix-ui-tpa/cssVars';
import { Location, Time } from '@wix/wix-ui-icons-common/odeditor';
import type { PopulatedMenu } from 'root/apiTypes';
import { dataHooks } from 'root/dataHooks';
import type { WidgetType } from 'root/components/MenuAddOn/Settings/Tabs/MenusTab/consts';
import { isOrderingWidgetType } from 'root/components/MenuAddOn/Settings/Tabs/MenusTab/consts';
import type { MenuAddOnOrderingBanner } from 'root/components/MenuAddOn/types';
import { useMenusContext } from 'root/contexts/MenusContext';
import { classes, st } from './Widget.st.css';

type EditorBannerAddress = {
  addressLine?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  formattedAddress?: string | null;
  streetAddress?: {
    name?: string | null;
    number?: string | null;
  } | null;
};

type EditorBannerLocationDetails = {
  address?: EditorBannerAddress;
  archived?: boolean | null;
  name?: string | null;
};

type OrderingBannerProps = {
  banner?: MenuAddOnOrderingBanner;
  isEditor: boolean;
  menus: PopulatedMenu[];
  widgetType: WidgetType;
  onChangeOrderingDispatch?: () => Promise<void>;
  onViewCart?: () => Promise<void>;
};

const getShortAddress = (address: string) => {
  const addressParts = address
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (addressParts.length <= 2) {
    return address;
  }

  const city = addressParts[addressParts.length - 3] ?? addressParts[addressParts.length - 2];

  return [addressParts[0], city].filter(Boolean).join(', ');
};

const getShortAddressText = (addressText: string) => {
  const separatorIndex = addressText.indexOf(':');

  if (separatorIndex === -1) {
    return getShortAddress(addressText);
  }

  const label = addressText.slice(0, separatorIndex + 1);
  const address = addressText.slice(separatorIndex + 1).trim();

  return `${label} ${getShortAddress(address)}`;
};

const getEditorBannerAddress = (menus: PopulatedMenu[]) => {
  const menuWithLocation = menus.find((menu) => menu.businessLocationDetails && !menu.businessLocationDetails.archived);
  const locationDetails = menuWithLocation?.businessLocationDetails as EditorBannerLocationDetails | undefined;
  const address = locationDetails?.address;

  if (address?.formattedAddress) {
    return address.formattedAddress;
  }

  if (address?.addressLine || address?.addressLine1) {
    return address.addressLine ?? address.addressLine1 ?? undefined;
  }

  const streetLine = [address?.streetAddress?.number, address?.streetAddress?.name].filter(Boolean).join(' ');
  const addressParts = [streetLine, address?.city].filter(Boolean);

  return addressParts.length ? addressParts.join(', ') : locationDetails?.name ?? menuWithLocation?.businessLocationId;
};

const getEditorOrderingBanner = (
  menus: PopulatedMenu[],
  t: (key: string, options?: Record<string, unknown>) => string
): MenuAddOnOrderingBanner => {
  const timeValue = t('app.menu-add-on.widget.ordering-banner.time.max', {
    minutes: 30,
  });
  const addressValue = getEditorBannerAddress(menus);

  return {
    addressText: addressValue
      ? t('app.menu-add-on.widget.ordering-banner.pickup-address', {
          address: addressValue,
        })
      : undefined,
    cartItemsCount: 1,
    timeText: t('app.menu-add-on.widget.ordering-banner.pickup-time', {
      time: timeValue,
    }),
  };
};

export const getOrderingBannerToDisplay = ({
  banner,
  isEditor,
  menus,
  t,
  widgetType,
}: Pick<OrderingBannerProps, 'banner' | 'isEditor' | 'menus' | 'widgetType'> & {
  t: (key: string, options?: Record<string, unknown>) => string;
}) =>
  isOrderingWidgetType(widgetType) ? banner ?? (isEditor ? getEditorOrderingBanner(menus, t) : undefined) : undefined;

export const shouldDisplayOrderingBanner = (widgetType: WidgetType, banner?: MenuAddOnOrderingBanner) =>
  isOrderingWidgetType(widgetType) &&
  !!banner &&
  banner.cartItemsCount > 0 &&
  (!!banner.timeText || !!banner.addressText);

export const OrderingBanner = ({
  banner,
  isEditor,
  menus,
  widgetType,
  onChangeOrderingDispatch,
  onViewCart,
}: OrderingBannerProps) => {
  const { t } = useTranslation();
  const { isMobile } = useMenusContext();
  const orderingBanner = getOrderingBannerToDisplay({ banner, isEditor, menus, t, widgetType });
  const shouldShowOrderingBanner = shouldDisplayOrderingBanner(widgetType, orderingBanner);
  const shortAddressText = orderingBanner?.addressText ? getShortAddressText(orderingBanner.addressText) : undefined;
  const changeButtonStyle = {
    borderRadius: 'var(--menuAddOnOrderingBannerChangeButtonBorderRadius)',
  } as React.CSSProperties;
  const viewCartButtonStyle = {
    borderRadius: 'var(--menuAddOnOrderingBannerViewCartButtonBorderRadius)',
  } as React.CSSProperties;
  const handleChangeOrderingDispatch = React.useCallback(() => {
    void onChangeOrderingDispatch?.();
  }, [onChangeOrderingDispatch]);
  const handleViewCart = React.useCallback(() => {
    void onViewCart?.();
  }, [onViewCart]);

  if (!shouldShowOrderingBanner || !orderingBanner) {
    return null;
  }

  return (
    <div
      className={st(classes.orderingBanner, isMobile ? classes.orderingBannerMobile : '')}
      data-hook={dataHooks.menuAddOnWidget.orderingBanner}
    >
      <div className={classes.orderingBannerInfo}>
        {orderingBanner.timeText && (
          <Text className={classes.orderingBannerInfoItem} typography={TextTypography.runningText}>
            <Time aria-hidden="true" />
            {orderingBanner.timeText}
          </Text>
        )}
        {orderingBanner.timeText && orderingBanner.addressText && <span className={classes.orderingBannerSeparator} />}
        {shortAddressText && (
          <Text className={classes.orderingBannerInfoItem} typography={TextTypography.runningText}>
            <Location aria-hidden="true" />
            <span className={classes.orderingBannerAddressText} title={orderingBanner.addressText}>
              {shortAddressText}
            </span>
          </Text>
        )}
      </div>
      <div className={classes.orderingBannerActions}>
        <div data-hook={dataHooks.menuAddOnWidget.orderingBannerChangeButton} onClick={handleChangeOrderingDispatch}>
          <Button
            upgrade
            className={classes.orderingBannerChangeButton}
            priority={ButtonPriority.secondary}
            style={changeButtonStyle}
            type="button"
          >
            {t('app.menu-add-on.widget.ordering-banner.change')}
          </Button>
        </div>
        <div data-hook={dataHooks.menuAddOnWidget.orderingBannerViewCartButton} onClick={handleViewCart}>
          <Button
            upgrade
            className={classes.orderingBannerViewCartButton}
            priority={ButtonPriority.primary}
            style={viewCartButtonStyle}
            type="button"
          >
            {t('app.menu-add-on.widget.ordering-banner.view-cart', {
              count: orderingBanner.cartItemsCount,
            })}
          </Button>
        </div>
      </div>
    </div>
  );
};
